<template>
    <div class="wine-release-landing" :class="{ 'is-ready': is_ready }">
        <branding-header />

        <div class="hero-section">
            <div class="release-badge">
                <v-icon color="amber darken-2" small>mdi-star</v-icon>
                <span>{{ wine_data.release_type }}</span>
            </div>

            <h1 class="wine-title">{{ wine_data.name }}</h1>

            <img class="wine-image" height="185" src="@/assets/barrel-creek-2024-sauv-blanc.png" alt="Lifestyle image of Barrel Creek 2024 Sauvignon Blanc" />
        </div>

        <div class="tasting-notes">
            <h3>Tasting Notes</h3>
            <p>{{ wine_data.tasting_notes }}</p>
        </div>

        <div class="pricing-section">
            <div class="price-comparison">
                <div class="regular-price">
                    <span class="label">Regular Price</span>
                    <span class="price strikethrough">${{ wine_data.regular_price.toFixed(2) }}</span>
                </div>
                <div class="club-price">
                    <span class="label">Your Club Price</span>
                    <span class="price highlight">${{ wine_data.club_price.toFixed(2) }}</span>
                </div>
            </div>
            <p class="savings-note">
                Save ${{ (wine_data.regular_price - wine_data.club_price).toFixed(2) }} per bottle!
            </p>
            <p class="allocation-note">
                <v-icon small color="primary">mdi-information</v-icon>
                Up to {{ wine_data.max_allocation }} bottles per member
            </p>
        </div>

        <div class="cta-section">
            <h3 class="cta-title">How many bottles would you like?</h3>
            <div class="quick-replies">
                <button
                    v-for="option in quick_reply_options"
                    :key="option.value"
                    class="quick-reply-btn"
                    :class="{ 'is-freeform': option.is_freeform }"
                    @click="handleQuickReply(option)"
                >
                    <v-icon small class="quick-reply-icon">{{ option.icon }}</v-icon>
                    <span>{{ option.label }}</span>
                </button>
            </div>

            <transition name="freeform-fade">
                <div v-if="show_freeform_input" class="freeform-input-container">
                    <input
                        ref="freeformInput"
                        v-model="freeform_text"
                        type="text"
                        class="freeform-input"
                        placeholder="How many bottles would you like?"
                        @keyup.enter="submitFreeform"
                    />
                    <button
                        class="freeform-send-btn"
                        :disabled="!freeform_text.trim()"
                        @click="submitFreeform"
                    >
                        <v-icon small>mdi-send</v-icon>
                    </button>
                </div>
            </transition>
        </div>

        <placeholder-dialog ref="placeholderDialog" />
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';

export default {
    name: 'MockWineReleaseLanding',

    components: {
        BrandingHeader,
        PlaceholderDialog
    },

    props: {
        wine_data: {
            type: Object,
            required: true
        },
        customer_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            is_ready: false,
            show_freeform_input: false,
            freeform_text: ''
        }
    },

    computed: {
        quick_reply_options() {
            return [
                { label: "I'll take 6!", value: 6, message: "I'll take 6!", icon: 'mdi-bottle-wine' },
                { label: "I'll take 4!", value: 4, message: "I'll take 4!", icon: 'mdi-bottle-wine' },
                { label: "I'll take 2!", value: 2, message: "I'll take 2!", icon: 'mdi-bottle-wine' },
                { label: 'Something else...', is_freeform: true, icon: 'mdi-pencil' }
            ];
        }
    },

    methods: {
        handleQuickReply(option) {
            if (option.is_freeform) {
                this.show_freeform_input = true;
                this.$nextTick(() => {
                    this.$refs.freeformInput?.focus();
                });
                return;
            }
            this.$emit('reserve', { quantity: option.value, message: option.message });
        },

        submitFreeform() {
            const text = this.freeform_text.trim();
            if (!text) return;

            this.$emit('reserve', { quantity: null, message: text });
            this.freeform_text = '';
            this.show_freeform_input = false;
        }
    },

    mounted() {
        requestAnimationFrame(() => {
            this.is_ready = true;
        });
    }
}
</script>

<style lang="scss" scoped>
.wine-release-landing {
    padding: 1rem;
    background: linear-gradient(180deg, #f8f5f0 0%, #ffffff 100%);
    min-height: 100%;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    &.is-ready {
        opacity: 1;
    }
}

.hero-section {
    text-align: center;
    margin-bottom: 1rem;
}

.release-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: linear-gradient(135deg, #5d4037 0%, #3e2723 100%);
    color: #ffd54f;
    padding: 0.35rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.75rem;

    span {
        padding-top: 2px;
    }
}

.wine-title {
    font-family: 'Georgia', serif;
    font-size: 1.4rem;
    color: #3e2723;
    margin-bottom: 1rem;
    line-height: 1.2;
}

.wine-image {
    max-width: 200px;
    object-fit: contain;
    margin: 0 auto;
}

.tasting-notes {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);

    h3 {
        font-size: 0.85rem;
        color: #5d4037;
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    p {
        font-size: 0.8rem;
        color: #666;
        line-height: 1.5;
        margin: 0;
    }
}

.pricing-section {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    text-align: center;
}

.price-comparison {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 0.5rem;
}

.regular-price, .club-price {
    display: flex;
    flex-direction: column;

    .label {
        font-size: 0.7rem;
        color: #999;
        text-transform: uppercase;
    }

    .price {
        font-size: 1.25rem;
        font-weight: 700;
    }
}

.regular-price .strikethrough {
    color: #999;
    text-decoration: line-through;
}

.club-price .highlight {
    color: var(--v-success-darken1);
}

.savings-note {
    color: var(--v-success-darken1);
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
}

.allocation-note {
    font-size: 0.75rem;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    margin: 0;
}

.cta {
    &-section {
        margin-bottom: 1rem;
    }

    &-title {
        font-size: 0.9rem;
        color: var(--v-primary-base);
        font-weight: 600;
        text-align: center;
        margin-bottom: 0.75rem;
    }
}

.quick-replies {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.quick-reply-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.7rem 1rem;
    background: #fff;
    border: 1.5px solid var(--v-primary-base);
    border-radius: 1.5rem;
    color: var(--v-primary-base);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    .quick-reply-icon {
        color: var(--v-primary-base);
    }

    &:hover {
        background: var(--v-primary-base);
        color: #fff;

        .quick-reply-icon {
            color: #fff;
        }
    }

    &:active {
        transform: scale(0.97);
    }

    &.is-freeform {
        background: transparent;
        border-style: dashed;

        &:hover {
            background: #f5f5f5;
            color: var(--v-primary-base);
            border-color: var(--v-primary-base);

            .quick-reply-icon {
                color: var(--v-primary-base);
            }
        }
    }
}

.freeform {
    &-input-container {
        display: flex;
        gap: 0.5rem;
        margin-top: 0.75rem;
    }

    &-input {
        flex: 1;
        padding: 0.6rem 1rem;
        border: 1.5px solid #ddd;
        border-radius: 1.5rem;
        font-size: 0.85rem;
        outline: none;
        transition: border-color 0.2s ease;

        &:focus {
            border-color: var(--v-primary-base);
        }
    }

    &-send-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: var(--v-primary-base);
        color: #fff;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.2s ease;

        .v-icon {
            color: #fff;
        }

        &:disabled {
            background: #ccc;
        }
    }

    &-fade-enter-active,
    &-fade-leave-active {
        transition: all 0.2s ease;
    }

    &-fade-enter,
    &-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
